import {
  type SupplierCertificateType,
  type SupplierIndustryKind,
  CAPABILITY_TAXONOMY
} from '@tandemone/types'

import {
  SUPPLIER_CERTIFICATE_LABELS,
  SUPPLIER_INDUSTRY_OPTIONS
} from '@/domains/supplier-onboarding'
import { COUNTRY_FILTER_OPTIONS } from '@/lib/countries'

type CompanyType = 'supplier' | 'buyer'

/** which directory sub-route the page is on. drives the title and seeds the
 * matching status filter; no longer sent to the server (the three statuses go
 * over the wire as independent booleans).
 */
export type SupplierDirectoryMode =
  | 'all'
  | 'favorites'
  | 'founding'
  | 'verified'

export type SupplierFilterState = {
  countries: string[]
  /** capability *group* keys (35-group taxonomy), the facet the server filters. */
  capabilityGroups: string[]
  industries: SupplierIndustryKind[]
  types: CompanyType[]
  certifications: SupplierCertificateType[]
  /** combinable status filters, directory-only (server-side). */
  founding: boolean
  verified: boolean
  favorites: boolean
}

export const EMPTY_FILTERS: SupplierFilterState = {
  countries: [],
  capabilityGroups: [],
  industries: [],
  types: [],
  certifications: [],
  founding: false,
  verified: false,
  favorites: false
}

export function isEmptyFilters(filters: SupplierFilterState): boolean {
  return (
    filters.countries.length === 0 &&
    filters.capabilityGroups.length === 0 &&
    filters.industries.length === 0 &&
    filters.types.length === 0 &&
    filters.certifications.length === 0 &&
    !filters.founding &&
    !filters.verified &&
    !filters.favorites
  )
}

/** the directory tabs (?tab=founding etc.) seed one status filter
 * on landing. centralized so the page, the empty-copy decision, and the
 * analytics "initial load" gate all agree on the baseline.
 */
export function seededFilters(
  mode: SupplierDirectoryMode
): SupplierFilterState {
  return {
    ...EMPTY_FILTERS,
    founding: mode === 'founding',
    verified: mode === 'verified',
    favorites: mode === 'favorites'
  }
}

export type Option<Value extends string> = { value: Value; label: string }

export type SupplierFilterOptions = {
  countries: Option<string>[]
  capabilityGroups: Option<string>[]
  industries: Option<SupplierIndustryKind>[]
  types: Option<CompanyType>[]
  certifications: Option<SupplierCertificateType>[]
}

/** every capability group (35), in taxonomy order (clustered by category) so
 * the facet list reads top-down rather than alphabetically scrambled. */
const CAPABILITY_GROUP_OPTIONS: Option<string>[] = CAPABILITY_TAXONOMY.flatMap(
  category =>
    category.groups.map(group => ({ value: group.key, label: group.label }))
)

const CAPABILITY_GROUP_LABELS: Record<string, string> = Object.fromEntries(
  CAPABILITY_GROUP_OPTIONS.map(option => [option.value, option.label])
)

/** the values a supplier row carries that the filters key off. both the
 * directory list item and the due-diligence list item satisfy this. `type` is
 * only present on the due-diligence item, so the Type facet only surfaces
 * there.
 */
type FilterableSupplier = {
  country: string | null
  capabilityGroups: string[]
  industries: SupplierIndustryKind[]
  type?: CompanyType
}

/** none_of_the_above is a sentinel meaning the supplier picked nothing from
 * the canonical list. surfacing it as a filter option would just be noise.
 */
function presentOptions<Kind extends string>(
  present: Set<Kind>,
  dict: Record<Kind, { label: string }>
): Option<Kind>[] {
  return [...present]
    .filter(kind => kind !== 'none_of_the_above')
    .map(value => ({ value, label: dict[value]?.label ?? value }))
    .sort((left, right) => left.label.localeCompare(right.label))
}

/** derive the available filter options from the rows actually in view, so
 * the sheet only offers values that exist in the current (mode-scoped)
 * table, with no dead options for countries/operations no supplier has.
 */
export function deriveSupplierFilterOptions(
  items: FilterableSupplier[]
): SupplierFilterOptions {
  const countries = new Set<string>()
  const capabilityGroups = new Set<string>()
  const industries = new Set<SupplierIndustryKind>()
  const types = new Set<CompanyType>()
  for (const item of items) {
    if (item.country) countries.add(item.country)
    for (const group of item.capabilityGroups) capabilityGroups.add(group)
    for (const industry of item.industries) industries.add(industry)
    if (item.type) types.add(item.type)
  }
  return {
    countries: [...countries]
      .sort((left, right) => left.localeCompare(right))
      .map(name => ({ value: name, label: name })),
    capabilityGroups: [...capabilityGroups]
      .filter(key => key !== 'none_of_the_above')
      .map(value => ({ value, label: CAPABILITY_GROUP_LABELS[value] ?? value }))
      .sort((left, right) => left.label.localeCompare(right.label)),
    industries: presentOptions(industries, SUPPLIER_INDUSTRY_OPTIONS),
    types: [...types]
      .sort((left, right) => left.localeCompare(right))
      .map(value => ({
        value,
        label: value === 'supplier' ? 'Supplier' : 'Buyer'
      })),
    certifications: []
  }
}

function allOptions<Kind extends string>(
  dict: Record<Kind, { label: string }>
): Option<Kind>[] {
  return (Object.keys(dict) as Kind[])
    .filter(kind => kind !== 'none_of_the_above')
    .map(value => ({ value, label: dict[value]?.label ?? value }))
    .sort((left, right) => left.label.localeCompare(right.label))
}

const CERTIFICATION_FILTER_OPTIONS = (
  Object.keys(SUPPLIER_CERTIFICATE_LABELS) as SupplierCertificateType[]
)
  .map(value => ({ value, label: SUPPLIER_CERTIFICATE_LABELS[value] }))
  .sort((left, right) => left.label.localeCompare(right.label))

/** the full canonical option sets for the (server-paginated) directory: every
 * country, capability group, and industry the platform supports, not just the
 * values present on the current page. the Type facet is supplier-only, hence
 * empty.
 */
export const SUPPLIER_DIRECTORY_FILTER_OPTIONS: SupplierFilterOptions = {
  countries: COUNTRY_FILTER_OPTIONS,
  capabilityGroups: CAPABILITY_GROUP_OPTIONS,
  industries: allOptions(SUPPLIER_INDUSTRY_OPTIONS),
  types: [],
  certifications: CERTIFICATION_FILTER_OPTIONS
}

/** canonical option sets for the (server-paginated) due-diligence queue. same
 * as the directory, but the queue spans both roles so the Type facet is
 * offered.
 */
export const DUE_DILIGENCE_FILTER_OPTIONS: SupplierFilterOptions = {
  countries: COUNTRY_FILTER_OPTIONS,
  capabilityGroups: CAPABILITY_GROUP_OPTIONS,
  industries: allOptions(SUPPLIER_INDUSTRY_OPTIONS),
  types: [
    { value: 'supplier', label: 'Supplier' },
    { value: 'buyer', label: 'Buyer' }
  ],
  certifications: CERTIFICATION_FILTER_OPTIONS
}

const URL_FACETS = [
  'countries',
  'capabilityGroups',
  'industries',
  'certifications',
  'types'
] as const

const URL_FACET_VALUES: Record<(typeof URL_FACETS)[number], Set<string>> = {
  countries: allowed(SUPPLIER_DIRECTORY_FILTER_OPTIONS.countries),
  capabilityGroups: allowed(SUPPLIER_DIRECTORY_FILTER_OPTIONS.capabilityGroups),
  industries: allowed(SUPPLIER_DIRECTORY_FILTER_OPTIONS.industries),
  certifications: allowed(SUPPLIER_DIRECTORY_FILTER_OPTIONS.certifications),
  types: allowed(DUE_DILIGENCE_FILTER_OPTIONS.types)
}

const URL_STATUSES = ['founding', 'verified', 'favorites'] as const

function allowed(options: Option<string>[]): Set<string> {
  return new Set(options.map(option => option.value))
}

/** filters from the url, keeping only values the filters offer so a typo never
 *  reaches the server. a status flag missing from the url falls back to the
 *  tab's preset, so ?tab=founding is founding until someone unticks it. */
export function filtersFromSearchParams(
  params: URLSearchParams,
  mode: SupplierDirectoryMode = 'all'
): SupplierFilterState {
  const filters = seededFilters(mode)
  for (const facet of URL_FACETS) {
    const values = (params.get(facet) ?? '')
      .split(',')
      .filter(value => URL_FACET_VALUES[facet].has(value))
    Object.assign(filters, { [facet]: values })
  }
  for (const status of URL_STATUSES) {
    const value = params.get(status)
    if (value === '1') filters[status] = true
    if (value === '0') filters[status] = false
  }
  return filters
}

function writeFilters(
  params: URLSearchParams,
  filters: SupplierFilterState,
  mode: SupplierDirectoryMode
): void {
  for (const facet of URL_FACETS) {
    if (filters[facet].length > 0) params.set(facet, filters[facet].join(','))
    else params.delete(facet)
  }
  const preset = seededFilters(mode)
  for (const status of URL_STATUSES) {
    if (filters[status] === preset[status]) params.delete(status)
    else params.set(status, filters[status] ? '1' : '0')
  }
}

/** writes only the filter keys and drops the page, since the result set
 *  changed. utm, gclid and the table's own search survive. */
export function withFilterSearchParams(
  params: URLSearchParams,
  filters: SupplierFilterState,
  mode: SupplierDirectoryMode = 'all'
): URLSearchParams {
  const next = new URLSearchParams(params)
  writeFilters(next, filters, mode)
  next.delete('page')
  return next
}

/** another tab: the status flags were written against the old tab's preset, so
 *  they go. facets and the search carry over. */
export function withDirectoryTab(
  params: URLSearchParams,
  mode: SupplierDirectoryMode
): URLSearchParams {
  const next = new URLSearchParams(params)
  if (mode === 'all') next.delete('tab')
  else next.set('tab', mode)
  for (const status of URL_STATUSES) next.delete(status)
  next.delete('page')
  return next
}

/** the url with its filter keys rewritten to what was actually kept, so a
 *  value nobody can pick (countries=Narnia) doesn't linger in the address. */
export function normalizeFilterSearchParams(
  params: URLSearchParams,
  mode: SupplierDirectoryMode = 'all'
): URLSearchParams {
  const next = new URLSearchParams(params)
  writeFilters(next, filtersFromSearchParams(params, mode), mode)
  return next
}
